import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Ng2IzitoastService } from 'ng2-izitoast';
import { forkJoin, of, Subject, Subscription } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, finalize, switchMap, tap } from 'rxjs/operators';
import { GruposService } from 'src/app/services/grupos.service';
import { LoginService } from 'src/app/services/login.service';

@Component({
  selector: 'app-grupos-v2',
  templateUrl: './grupos-v2.component.html',
  styleUrls: ['./grupos-v2.component.scss'],
})
export class GruposV2Component implements OnInit, OnDestroy {
  grupos: any[] = [];
  miembros: any[] = [];
  datosCliente: any = {};
  grupoMiembros: any = null;
  form: FormGroup;
  formSubmitted = false;
  modoEdicion = false;
  grupoEditando: any = null;
  loadingGrupos = false;
  loadingMiembros = false;
  guardando = false;
  accionGrupo: any = null;
  errorMensaje = '';
  catalogKeyword = 'nombre';
  autocompleteNoFilter = (items: any[], query: string): any[] => items;

  filtros: any = {
    q: '',
    estatus: '1',
    categoria: '',
    vendedor: '',
    page: 1,
    page_size: 10,
  };

  meta: any = {
    page: 1,
    page_size: 10,
    total: 0,
    total_pages: 1,
    has_next: false,
    has_previous: false,
  };

  catalogos: any = {
    escuelas: this.catalogoBase(),
    carreras: this.catalogoBase(),
    categorias: this.catalogoBase(),
    vendedores: this.catalogoBase(),
  };

  private escuelasSearch$ = new Subject<string>();
  private carrerasSearch$ = new Subject<string>();
  private categoriasSearch$ = new Subject<string>();
  private vendedoresSearch$ = new Subject<string>();
  private subscriptions: Subscription[] = [];

  constructor(
    private modalService: NgbModal,
    private fb: FormBuilder,
    private gruposService: GruposService,
    private loginService: LoginService,
    public iziToast: Ng2IzitoastService
  ) {}

  ngOnInit(): void {
    this.form = this.fb.group({
      nombre_grupo: ['', [Validators.required, Validators.minLength(2)]],
      institucion: ['', Validators.required],
      carrera: ['', Validators.required],
      vendedor: ['', Validators.required],
      categoria: ['', Validators.required],
    });

    this.configurarBuscadorCatalogo('escuelas', this.escuelasSearch$);
    this.configurarBuscadorCatalogo('carreras', this.carrerasSearch$);
    this.configurarBuscadorCatalogo('categorias', this.categoriasSearch$);
    this.configurarBuscadorCatalogo('vendedores', this.vendedoresSearch$);

    this.cargarCatalogosIniciales();
    this.cargarDatosCliente();
    this.cargarGrupos();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription: Subscription) => subscription.unsubscribe());
  }

  private catalogoBase(): any {
    return {
      data: [],
      selected: null,
      q: '',
      page: 1,
      page_size: 10,
      total: 0,
      total_pages: 1,
      has_next: false,
      loading: false,
    };
  }

  private configurarBuscadorCatalogo(tipo: string, subject: Subject<string>): void {
    const state = this.catalogos[tipo];
    const subscription = subject.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      tap((term: string) => {
        state.q = term || '';
        state.page = 1;
        state.loading = true;
      }),
      switchMap((term: string) =>
        this.gruposService.buscarCatalogoGrupos(tipo, {
          q: term || '',
          page: 1,
          page_size: state.page_size,
        }).pipe(
          catchError((error) => {
            console.error(error);
            this.toast('No se pudo buscar catalogo.', '#CA0B00');
            return of({ success: 0, content: [] });
          }),
          finalize(() => state.loading = false)
        )
      )
    ).subscribe((data: any) => this.aplicarCatalogo(tipo, data, false));

    this.subscriptions.push(subscription);
  }

  cargarCatalogosIniciales(): void {
    const params = { page: 1, page_size: 10 };
    const request = forkJoin([
      this.gruposService.buscarCatalogoGrupos('escuelas', params).pipe(catchError(() => of({ success: 0, content: [] }))),
      this.gruposService.buscarCatalogoGrupos('carreras', params).pipe(catchError(() => of({ success: 0, content: [] }))),
      this.gruposService.buscarCatalogoGrupos('categorias', params).pipe(catchError(() => of({ success: 0, content: [] }))),
      this.gruposService.buscarCatalogoGrupos('vendedores', params).pipe(catchError(() => of({ success: 0, content: [] }))),
    ]);

    this.subscriptions.push(
      request.subscribe((respuestas: any[]) => {
        this.aplicarCatalogo('escuelas', respuestas[0], false);
        this.aplicarCatalogo('carreras', respuestas[1], false);
        this.aplicarCatalogo('categorias', respuestas[2], false);
        this.aplicarCatalogo('vendedores', respuestas[3], false);
      })
    );
  }

  cargarDatosCliente(): void {
    this.loginService.getDataCliente().pipe(
      catchError((error) => {
        console.error(error);
        return of({ success: 0, content: {} });
      })
    ).subscribe((data: any) => {
      if (data.success) {
        this.datosCliente = data.content || {};
      }
    });
  }

  cargarGrupos(page: number = 1): void {
    this.loadingGrupos = true;
    this.errorMensaje = '';
    this.filtros.page = page;

    this.gruposService.getGruposV2(this.filtros).pipe(
      catchError((error) => {
        console.error(error);
        this.errorMensaje = 'No se pudieron cargar grupos.';
        return of({ success: 0, content: [] });
      }),
      finalize(() => this.loadingGrupos = false)
    ).subscribe((data: any) => {
      if (data.success) {
        this.grupos = data.content || [];
        this.meta = {
          page: data.page || 1,
          page_size: data.page_size || this.filtros.page_size,
          total: data.total || 0,
          total_pages: data.total_pages || 1,
          has_next: !!data.has_next,
          has_previous: !!data.has_previous,
        };
      } else {
        this.errorMensaje = this.extraerError(data, 'No se pudieron cargar grupos.');
      }
    });
  }

  aplicarFiltros(): void {
    this.cargarGrupos(1);
  }

  limpiarFiltros(): void {
    this.filtros = {
      q: '',
      estatus: '1',
      categoria: '',
      vendedor: '',
      page: 1,
      page_size: 10,
    };
    this.cargarGrupos(1);
  }

  cambiarPagina(page: number): void {
    if (page < 1 || page > this.meta.total_pages || page === this.meta.page) {
      return;
    }
    this.cargarGrupos(page);
  }

  get paginas(): number[] {
    const total = this.meta.total_pages || 1;
    const current = this.meta.page || 1;
    let start = Math.max(1, current - 2);
    const end = Math.min(total, start + 4);
    if (end - start < 4) {
      start = Math.max(1, end - 4);
    }

    const pages = [];
    for (let page = start; page <= end; page++) {
      pages.push(page);
    }
    return pages;
  }

  abrirNuevo(modal): void {
    this.modoEdicion = false;
    this.grupoEditando = null;
    this.formSubmitted = false;
    this.errorMensaje = '';
    this.form.reset({
      nombre_grupo: '',
      institucion: '',
      carrera: '',
      vendedor: '',
      categoria: '',
    });
    this.limpiarSeleccionCatalogos();
    this.modalService.open(modal, { size: 'lg', windowClass: 'grupos-modal' });
  }

  abrirEditar(modal, grupo: any): void {
    this.modoEdicion = true;
    this.grupoEditando = grupo;
    this.formSubmitted = false;
    this.errorMensaje = '';
    this.form.reset({
      nombre_grupo: grupo.nombre || grupo.nombre_grupo || '',
      institucion: grupo.escuela_id || '',
      carrera: grupo.carrera_id || '',
      vendedor: grupo.vendedor_id || '',
      categoria: grupo.categoria_id || '',
    });

    this.setSeleccionCatalogo('escuelas', { id: grupo.escuela_id, nombre: grupo.escuela });
    this.setSeleccionCatalogo('carreras', { id: grupo.carrera_id, nombre: grupo.carrera });
    this.setSeleccionCatalogo('categorias', { id: grupo.categoria_id, nombre: grupo.categoria });
    this.setSeleccionCatalogo('vendedores', { id: grupo.vendedor_id, nombre: grupo.vendedor });

    this.modalService.open(modal, { size: 'lg', windowClass: 'grupos-modal' });
  }

  guardarGrupo(): void {
    this.formSubmitted = true;
    this.errorMensaje = '';

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.errorMensaje = 'Revise campos obligatorios.';
      return;
    }

    const payload = this.form.value;
    const request = this.modoEdicion
      ? this.gruposService.actualizarGrupoV2(this.grupoEditando.id, payload)
      : this.gruposService.crearGrupoV2(payload);

    this.guardando = true;
    request.pipe(
      catchError((error) => {
        console.error(error);
        return of({ success: 0, error: 'No se pudo guardar grupo.' });
      }),
      finalize(() => this.guardando = false)
    ).subscribe((data: any) => {
      if (data.success) {
        this.toast(data.mensaje || 'Grupo guardado.', '#4CAF50');
        this.dismissAll();
        this.cargarGrupos(this.meta.page || 1);
      } else {
        this.errorMensaje = this.extraerError(data, 'No se pudo guardar grupo.');
      }
    });
  }

  cerrarGrupo(grupo: any): void {
    if (!confirm('Cerrar grupo "' + grupo.nombre + '"?')) {
      return;
    }

    this.accionGrupo = grupo.id;
    this.gruposService.cerrarGrupo({ grupo: grupo.id }).pipe(
      catchError((error) => {
        console.error(error);
        return of({ success: 0, error: 'No se pudo cerrar grupo.' });
      }),
      finalize(() => this.accionGrupo = null)
    ).subscribe((data: any) => {
      if (data.success) {
        this.toast('Grupo cerrado.', '#4CAF50');
        this.cargarGrupos(this.meta.page || 1);
      } else {
        this.toast(this.extraerError(data, 'No se pudo cerrar grupo.'), '#CA0B00');
      }
    });
  }

  eliminarGrupo(grupo: any): void {
    if (!confirm('Eliminar grupo "' + grupo.nombre + '"?')) {
      return;
    }

    this.accionGrupo = grupo.id;
    this.gruposService.eliminarGrupoV2(grupo.id).pipe(
      catchError((error) => {
        console.error(error);
        return of({ success: 0, error: 'No se pudo eliminar grupo.' });
      }),
      finalize(() => this.accionGrupo = null)
    ).subscribe((data: any) => {
      if (data.success) {
        this.toast(data.mensaje || 'Grupo eliminado.', '#4CAF50');
        this.cargarGrupos(this.meta.page || 1);
      } else {
        this.toast(this.extraerError(data, 'No se pudo eliminar grupo.'), '#CA0B00');
      }
    });
  }

  getMiembrosGrupo(modal, grupo: any): void {
    this.grupoMiembros = grupo;
    this.miembros = [];
    this.loadingMiembros = true;
    this.modalService.open(modal, { size: 'lg' });

    this.gruposService.getMiembros({ grupo: grupo.id }).pipe(
      catchError((error) => {
        console.error(error);
        return of({ success: 0, error: 'No se pudieron cargar miembros.' });
      }),
      finalize(() => this.loadingMiembros = false)
    ).subscribe((data: any) => {
      if (data.success) {
        this.miembros = data.content || [];
      } else {
        this.toast(this.extraerError(data, 'No se pudieron cargar miembros.'), '#CA0B00');
      }
    });
  }

  onCatalogInput(tipo: string, value: string): void {
    const control = this.controlCatalogo(tipo);
    this.catalogos[tipo].selected = null;
    this.form.get(control).setValue('');
    this.form.get(control).markAsTouched();
    this.subjectCatalogo(tipo).next(value || '');
  }

  onCatalogFocus(tipo: string): void {
    const state = this.catalogos[tipo];
    if (!state.data.length) {
      this.buscarCatalogo(tipo, state.q || '', false);
    }
  }

  seleccionarCatalogo(tipo: string, item: any): void {
    if (!item) {
      return;
    }
    const control = this.controlCatalogo(tipo);
    this.catalogos[tipo].selected = item;
    this.form.get(control).setValue(item.id);
    this.form.get(control).markAsTouched();
  }

  limpiarCatalogo(tipo: string): void {
    const control = this.controlCatalogo(tipo);
    this.catalogos[tipo].selected = null;
    this.form.get(control).setValue('');
    this.form.get(control).markAsTouched();
    this.subjectCatalogo(tipo).next('');
  }

  cargarMasCatalogo(tipo: string): void {
    const state = this.catalogos[tipo];
    if (state.loading || !state.has_next) {
      return;
    }
    this.buscarCatalogo(tipo, state.q || '', true);
  }

  private buscarCatalogo(tipo: string, query: string, append: boolean): void {
    const state = this.catalogos[tipo];
    state.loading = true;
    const page = append ? state.page + 1 : 1;
    this.gruposService.buscarCatalogoGrupos(tipo, {
      q: query,
      page: page,
      page_size: state.page_size,
    }).pipe(
      catchError((error) => {
        console.error(error);
        this.toast('No se pudo cargar catalogo.', '#CA0B00');
        return of({ success: 0, content: [] });
      }),
      finalize(() => state.loading = false)
    ).subscribe((data: any) => this.aplicarCatalogo(tipo, data, append));
  }

  private aplicarCatalogo(tipo: string, data: any, append: boolean): void {
    const state = this.catalogos[tipo];
    const content = data && data.success ? (data.content || []) : [];
    state.data = append ? state.data.concat(content) : content;
    state.page = data.page || state.page || 1;
    state.total = data.total || 0;
    state.total_pages = data.total_pages || 1;
    state.has_next = !!data.has_next;
    if (state.selected) {
      this.setSeleccionCatalogo(tipo, state.selected);
    }
  }

  private setSeleccionCatalogo(tipo: string, item: any): void {
    if (!item || !item.id) {
      this.catalogos[tipo].selected = null;
      return;
    }
    const state = this.catalogos[tipo];
    state.selected = item;
    const exists = state.data.some((current: any) => Number(current.id) === Number(item.id));
    if (!exists) {
      state.data = [item].concat(state.data || []);
    }
  }

  private limpiarSeleccionCatalogos(): void {
    Object.keys(this.catalogos).forEach((tipo: string) => {
      this.catalogos[tipo].selected = null;
    });
  }

  private controlCatalogo(tipo: string): string {
    const map = {
      escuelas: 'institucion',
      carreras: 'carrera',
      categorias: 'categoria',
      vendedores: 'vendedor',
    };
    return map[tipo];
  }

  private subjectCatalogo(tipo: string): Subject<string> {
    if (tipo === 'escuelas') {
      return this.escuelasSearch$;
    }
    if (tipo === 'carreras') {
      return this.carrerasSearch$;
    }
    if (tipo === 'categorias') {
      return this.categoriasSearch$;
    }
    return this.vendedoresSearch$;
  }

  controlInvalido(control: string): boolean {
    const field = this.form.get(control);
    return !!field && field.invalid && (field.touched || this.formSubmitted);
  }

  extraerError(data: any, fallback: string): string {
    if (!data || !data.error) {
      return fallback;
    }
    if (typeof data.error === 'string') {
      return data.error;
    }
    if (data.errors) {
      const firstKey = Object.keys(data.errors)[0];
      const firstValue = firstKey ? data.errors[firstKey] : '';
      return Array.isArray(firstValue) ? firstValue[0] : (firstValue || fallback);
    }
    return fallback;
  }

  tieneAccesoPagos(): boolean {
    return this.datosCliente && this.datosCliente.acceso_pagos === 1;
  }

  trackByGrupo(index: number, grupo: any): number {
    return grupo.id;
  }

  dismissAll(): void {
    this.modalService.dismissAll();
  }

  toast(title: string, color: string): void {
    this.iziToast.show({
      title: title,
      titleColor: '#f8f8ff',
      color: color,
      position: 'topRight',
      closeOnEscape: true,
    });
  }
}
